import { createApi } from '@reduxjs/toolkit/query/react';
import { Response, UpdateDTO } from '../../models/shared';
import { transformResponseData } from '../utils';
import CardPromotionDTO, { CreateCardPromotionDTO, PublicCardPromotionDTO } from '../../types/CardPromotion';
import baseQueryWithReauth from './base';

const cardPromotionsApi = createApi({
  reducerPath: 'cardPromotionsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['card_promotions', 'event_card_promotions'],
  endpoints: (build) => ({
    getCardPromotions: build.query<CardPromotionDTO[] | null, void>({
      providesTags: ['card_promotions'],
      query: () => ({ url: '/card-promotions', method: 'GET' }),
      transformResponse: (response: Response<CardPromotionDTO[]>): (CardPromotionDTO[] | null) => response.data || null,
    }),
    createCardPromotion: build.mutation<Response<CardPromotionDTO>, { promotion: CreateCardPromotionDTO }>({
      query: ({ promotion }) => ({ url: '/card-promotions', method: 'POST', body: promotion }),
      invalidatesTags: ['card_promotions', 'event_card_promotions'],
    }),
    updateCardPromotion: build.mutation<Response<CardPromotionDTO>, { promotion: UpdateDTO<CardPromotionDTO> }>({
      query: ({ promotion }) => ({ url: '/card-promotions', method: 'PUT', body: promotion }),
      invalidatesTags: ['card_promotions', 'event_card_promotions'],
    }),
    deleteCardPromotion: build.mutation<CardPromotionDTO | null, { cardPromotionId: string }>({
      query: ({ cardPromotionId }) => ({ url: `/card-promotions/${cardPromotionId}`, method: 'DELETE' }),
      transformResponse: transformResponseData,
      invalidatesTags: ['card_promotions', 'event_card_promotions'],
    }),
    getEventCardPromotions: build.query<PublicCardPromotionDTO[] | null, { businessId: string, eventId: string }>({
      providesTags: (result, error, { eventId }) => [{ type: 'event_card_promotions', id: eventId }],
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/card-promotions`,
        method: 'GET',
      }),
      transformResponse: (response: Response<PublicCardPromotionDTO[]>): (PublicCardPromotionDTO[] | null) => response.data || null,
    }),
  }),
});

export const {
  useGetCardPromotionsQuery,
  useCreateCardPromotionMutation,
  useUpdateCardPromotionMutation,
  useDeleteCardPromotionMutation,
  useGetEventCardPromotionsQuery,
} = cardPromotionsApi;

export { cardPromotionsApi };
